import createClient from 'openapi-fetch';
import { effectiveTenantId, useAuthStore } from '@/auth/authStore';
import { env } from '@/lib/env';
import { createAuthFetch } from './authFetch';
import type { paths } from './schema';

const authFetch = createAuthFetch({
  baseUrl: env.apiBaseUrl,
  getAccessToken: () => useAuthStore.getState().accessToken,
  getRefreshToken: () => useAuthStore.getState().refreshToken,
  getTenantId: () => effectiveTenantId(useAuthStore.getState()),
  onRefreshed: (tokens) => useAuthStore.getState().setSession(tokens),
  onAuthFailed: () => useAuthStore.getState().clear(),
});

/**
 * The typed platform API client. The UI talks to the backend only through this (CLAUDE.md rule 9).
 * Combine with `unwrap()` from './problem' inside TanStack Query functions.
 */
export const api = createClient<paths>({ baseUrl: env.apiBaseUrl, fetch: authFetch });

export interface RawRequestOptions {
  query?: Record<string, string | number | boolean | undefined | null>;
  body?: unknown;
  headers?: Record<string, string>;
  contentType?: string;
}

export interface RawResult<T> {
  data?: T;
  error?: unknown;
  response: Response;
}

/**
 * Escape hatch for endpoints whose generated types are too loose (JSON documents typed as
 * `Record<string, never>`) or that need extra headers like If-Match. Same auth/tenant handling as `api`.
 * Combine with `unwrap()`.
 */
export async function apiRequest<T>(method: string, path: string, opts: RawRequestOptions = {}): Promise<RawResult<T>> {
  const url = new URL(env.apiBaseUrl + path);
  for (const [k, v] of Object.entries(opts.query ?? {})) if (v !== undefined && v !== null) url.searchParams.set(k, String(v));
  const headers = new Headers(opts.headers);
  let body: BodyInit | undefined;
  if (opts.body !== undefined) {
    headers.set('Content-Type', opts.contentType ?? 'application/json');
    body = JSON.stringify(opts.body);
  }
  const response = await authFetch(new Request(url, { method, headers, body }));
  const text = await response.text();
  let parsed: unknown = undefined;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }
  return response.ok ? { data: parsed as T, response } : { error: parsed ?? {}, response };
}

export { unwrap, ApiError, errorMessage } from './problem';
